<script setup>
import { ref, computed, onMounted } from 'vue';
import { useMapGetter } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import SectionLayout from './SectionLayout.vue';
import WithLabel from 'v3/components/Form/WithLabel.vue';
import Input from 'next/input/Input.vue';
import Switch from 'next/switch/Switch.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import AppointmentReminderTemplatesAPI from 'dashboard/api/appointmentReminderTemplates';

const { t } = useI18n();
const templates = ref([]);
const isLoading = ref(false);
const showForm = ref(false);
const isSubmitting = ref(false);
const editingTemplate = ref(null);

// Form fields
const formName = ref('');
const formMessage = ref('');
const formInboxId = ref(null);
const formRemindBefore = ref(30);
const formActive = ref(true);

const inboxes = useMapGetter('inboxes/getInboxes');

const reminderMinutesOptions = [
  { value: 5, label: '5 minutos antes' },
  { value: 15, label: '15 minutos antes' },
  { value: 30, label: '30 minutos antes' },
  { value: 60, label: '1 hora antes' },
  { value: 120, label: '2 horas antes' },
  { value: 1440, label: '1 dia antes' },
  { value: 2880, label: '2 dias antes' },
];

const inboxOptions = computed(() => {
  if (!inboxes.value) return [];
  return inboxes.value
    .filter(inbox =>
      [
        'Channel::Whatsapp',
        'Channel::Api',
        'Channel::TwilioSms',
        'Channel::Sms',
      ].includes(inbox.channel_type)
    )
    .map(inbox => ({
      value: inbox.id,
      label: inbox.name,
    }));
});

const defaultTemplate = `Olá [contact_name], lembrando do seu compromisso '[appointment_title]' em [appointment_date] às [appointment_time].`;

const isMessageExpanded = ref(false);

// Variables available for templates - use computed to react to locale changes
const templateVariables = computed(() => [
  {
    key: '[contact_name]',
    description: t('APPOINTMENT_REMINDER_SETTINGS.VARIABLES.CONTACT_NAME_DESC'),
  },
  {
    key: '[appointment_title]',
    description: t('APPOINTMENT_REMINDER_SETTINGS.VARIABLES.TITLE_DESC'),
  },
  {
    key: '[appointment_date]',
    description: t('APPOINTMENT_REMINDER_SETTINGS.VARIABLES.DATE_DESC'),
  },
  {
    key: '[appointment_time]',
    description: t('APPOINTMENT_REMINDER_SETTINGS.VARIABLES.TIME_DESC'),
  },
  {
    key: '[appointment_location]',
    description: t('APPOINTMENT_REMINDER_SETTINGS.VARIABLES.LOCATION_DESC'),
  },
  {
    key: '[appointment_description]',
    description: t('APPOINTMENT_REMINDER_SETTINGS.VARIABLES.DESCRIPTION_DESC'),
  },
]);

const insertVariable = variable => {
  formMessage.value += variable;
};

const toggleMessageExpand = () => {
  isMessageExpanded.value = !isMessageExpanded.value;
};

const getRemindBeforeLabel = value => {
  const option = reminderMinutesOptions.find(o => o.value === value);
  return option ? option.label : `${value} minutos antes`;
};

const fetchTemplates = async () => {
  try {
    isLoading.value = true;
    const response = await AppointmentReminderTemplatesAPI.get();
    templates.value = response.data;
  } catch (error) {
    useAlert(t('APPOINTMENT_REMINDER_SETTINGS.TEMPLATES.LOAD_ERROR'));
  } finally {
    isLoading.value = false;
  }
};

const resetForm = () => {
  formName.value = '';
  formMessage.value = defaultTemplate;
  formInboxId.value = null;
  formRemindBefore.value = 30;
  formActive.value = true;
  editingTemplate.value = null;
};

const openNewForm = () => {
  resetForm();
  showForm.value = true;
};

const openEditForm = template => {
  formName.value = template.name;
  formMessage.value = template.message_template;
  formInboxId.value = template.inbox_id;
  formRemindBefore.value = template.remind_before;
  formActive.value = template.active;
  editingTemplate.value = template;
  showForm.value = true;
};

const closeForm = () => {
  showForm.value = false;
  resetForm();
};

const handleSubmit = async () => {
  if (!formName.value.trim() || !formMessage.value.trim()) {
    useAlert(t('APPOINTMENT_REMINDER_SETTINGS.TEMPLATES.VALIDATION_ERROR'));
    return;
  }

  try {
    isSubmitting.value = true;
    const data = {
      name: formName.value,
      message_template: formMessage.value,
      inbox_id: formInboxId.value,
      remind_before: formRemindBefore.value,
      active: formActive.value,
    };

    if (editingTemplate.value) {
      await AppointmentReminderTemplatesAPI.update(
        editingTemplate.value.id,
        data
      );
      useAlert(t('APPOINTMENT_REMINDER_SETTINGS.TEMPLATES.UPDATE_SUCCESS'));
    } else {
      await AppointmentReminderTemplatesAPI.create(data);
      useAlert(t('APPOINTMENT_REMINDER_SETTINGS.TEMPLATES.CREATE_SUCCESS'));
    }

    await fetchTemplates();
    closeForm();
  } catch (error) {
    const errorMsg =
      error.response?.data?.errors?.join(', ') ||
      t('APPOINTMENT_REMINDER_SETTINGS.TEMPLATES.SAVE_ERROR');
    useAlert(errorMsg);
  } finally {
    isSubmitting.value = false;
  }
};

const deleteTemplate = async template => {
  if (
    !confirm(
      t('APPOINTMENT_REMINDER_SETTINGS.TEMPLATES.DELETE_CONFIRM', {
        name: template.name,
      })
    )
  ) {
    return;
  }

  try {
    await AppointmentReminderTemplatesAPI.delete(template.id);
    useAlert(t('APPOINTMENT_REMINDER_SETTINGS.TEMPLATES.DELETE_SUCCESS'));
    await fetchTemplates();
  } catch (error) {
    useAlert(t('APPOINTMENT_REMINDER_SETTINGS.TEMPLATES.DELETE_ERROR'));
  }
};

onMounted(() => {
  fetchTemplates();
});
</script>

<template>
  <SectionLayout
    :title="t('APPOINTMENT_REMINDER_SETTINGS.TITLE')"
    :description="t('APPOINTMENT_REMINDER_SETTINGS.DESCRIPTION')"
    with-border
  >
    <!-- Templates List -->
    <div v-if="!showForm" class="space-y-4">
      <div class="flex justify-between items-center">
        <h4 class="text-sm font-medium text-n-slate-12">
          {{ t('APPOINTMENT_REMINDER_SETTINGS.TEMPLATES.LIST_TITLE') }}
        </h4>
        <NextButton
          blue
          size="sm"
          icon="i-lucide-plus"
          :label="t('APPOINTMENT_REMINDER_SETTINGS.TEMPLATES.ADD')"
          @click="openNewForm"
        />
      </div>

      <div v-if="isLoading" class="flex justify-center py-8">
        <span class="text-n-slate-11">{{
          t('APPOINTMENT_REMINDER_SETTINGS.TEMPLATES.LOADING')
        }}</span>
      </div>

      <div
        v-else-if="templates.length === 0"
        class="text-center py-8 text-n-slate-11"
      >
        {{ t('APPOINTMENT_REMINDER_SETTINGS.TEMPLATES.EMPTY') }}
      </div>

      <div v-else class="space-y-3">
        <div
          v-for="template in templates"
          :key="template.id"
          class="border border-n-weak rounded-lg p-4 bg-n-solid-1"
        >
          <div class="flex justify-between items-start gap-4">
            <div class="flex-1 min-w-0">
              <div class="flex items-center gap-2 mb-1">
                <h5 class="font-medium text-n-slate-12 truncate">
                  {{ template.name }}
                </h5>
                <span
                  class="px-2 py-0.5 text-xs rounded-full"
                  :class="[
                    template.active
                      ? 'bg-n-teal-3 text-n-teal-11'
                      : 'bg-n-slate-3 text-n-slate-11',
                  ]"
                >
                  {{
                    template.active
                      ? t('APPOINTMENT_REMINDER_SETTINGS.TEMPLATES.ACTIVE')
                      : t('APPOINTMENT_REMINDER_SETTINGS.TEMPLATES.INACTIVE')
                  }}
                </span>
              </div>
              <p class="text-sm text-n-slate-11 line-clamp-2 mb-2">
                {{ template.message_template }}
              </p>
              <div class="flex flex-wrap gap-3 text-xs text-n-slate-10">
                <span class="flex items-center gap-1">
                  <i class="i-lucide-inbox w-3.5 h-3.5" />
                  {{
                    template.inbox
                      ? template.inbox.name
                      : t('APPOINTMENT_REMINDER_SETTINGS.TEMPLATES.ANY_INBOX')
                  }}
                </span>
                <span class="flex items-center gap-1">
                  <i class="i-lucide-clock w-3.5 h-3.5" />
                  {{ getRemindBeforeLabel(template.remind_before) }}
                </span>
              </div>
            </div>
            <div class="flex gap-2">
              <NextButton
                size="sm"
                faded
                icon="i-lucide-pencil"
                @click="openEditForm(template)"
              />
              <NextButton
                size="sm"
                faded
                ruby
                icon="i-lucide-trash-2"
                @click="deleteTemplate(template)"
              />
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Template Form -->
    <div v-else>
      <div class="flex items-center gap-2 mb-4">
        <NextButton
          faded
          size="sm"
          icon="i-lucide-arrow-left"
          @click="closeForm"
        />
        <h4 class="text-sm font-medium text-n-slate-12">
          {{
            editingTemplate
              ? t('APPOINTMENT_REMINDER_SETTINGS.TEMPLATES.EDIT_TITLE')
              : t('APPOINTMENT_REMINDER_SETTINGS.TEMPLATES.ADD_TITLE')
          }}
        </h4>
      </div>

      <form class="grid gap-5" @submit.prevent="handleSubmit">
        <WithLabel :label="t('APPOINTMENT_REMINDER_SETTINGS.TEMPLATES.NAME')">
          <Input
            v-model="formName"
            class="w-full"
            :placeholder="
              t('APPOINTMENT_REMINDER_SETTINGS.TEMPLATES.NAME_PLACEHOLDER')
            "
          />
        </WithLabel>

        <div class="form-group">
          <label class="block text-sm font-medium text-n-slate-12 mb-2">
            {{ t('APPOINTMENT_REMINDER_SETTINGS.TEMPLATES.MESSAGE') }}
          </label>
          <div class="message-container">
            <div class="textarea-wrapper">
              <!-- Overlay backdrop when expanded -->
              <div
                v-if="isMessageExpanded"
                class="message-overlay"
                @click="toggleMessageExpand"
              />
              <textarea
                v-model="formMessage"
                class="message-textarea"
                :class="{ expanded: isMessageExpanded }"
                :rows="isMessageExpanded ? 12 : 4"
                :placeholder="defaultTemplate"
              />
              <button
                type="button"
                class="expand-btn"
                :class="{ 'expanded-btn': isMessageExpanded }"
                :title="
                  isMessageExpanded
                    ? t('APPOINTMENT_REMINDER_SETTINGS.TEMPLATES.COLLAPSE')
                    : t('APPOINTMENT_REMINDER_SETTINGS.TEMPLATES.EXPAND')
                "
                @click="toggleMessageExpand"
              >
                <i
                  :class="
                    isMessageExpanded
                      ? 'i-lucide-minimize-2'
                      : 'i-lucide-maximize-2'
                  "
                  class="w-4 h-4"
                />
              </button>
            </div>

            <p class="text-xs text-n-slate-10 mt-3 mb-2">
              {{ t('APPOINTMENT_REMINDER_SETTINGS.VARIABLES_HELP') }}
            </p>

            <div class="variables-grid">
              <div
                v-for="variable in templateVariables"
                :key="variable.key"
                class="variable-item"
              >
                <button
                  type="button"
                  class="variable-btn"
                  :title="
                    t('APPOINTMENT_REMINDER_SETTINGS.TEMPLATES.INSERT_VARIABLE')
                  "
                  @click="insertVariable(variable.key)"
                >
                  <code class="variable-code">{{ variable.key }}</code>
                  <i
                    class="i-lucide-plus w-3 h-3 opacity-0 group-hover:opacity-100"
                  />
                </button>
                <span class="variable-desc">{{ variable.description }}</span>
              </div>
            </div>
          </div>
        </div>

        <WithLabel :label="t('APPOINTMENT_REMINDER_SETTINGS.TEMPLATES.INBOX')">
          <select
            v-model="formInboxId"
            class="w-full rounded-lg border border-n-weak bg-n-solid-2 px-3 py-2 text-sm"
          >
            <option :value="null">
              {{ t('APPOINTMENT_REMINDER_SETTINGS.TEMPLATES.ANY_INBOX') }}
            </option>
            <option
              v-for="inbox in inboxOptions"
              :key="inbox.value"
              :value="inbox.value"
            >
              {{ inbox.label }}
            </option>
          </select>
          <p class="text-xs text-n-slate-10 mt-1">
            {{ t('APPOINTMENT_REMINDER_SETTINGS.INBOX_HELP') }}
          </p>
        </WithLabel>

        <WithLabel
          :label="t('APPOINTMENT_REMINDER_SETTINGS.TEMPLATES.REMIND_BEFORE')"
        >
          <select
            v-model="formRemindBefore"
            class="w-full rounded-lg border border-n-weak bg-n-solid-2 px-3 py-2 text-sm"
          >
            <option
              v-for="option in reminderMinutesOptions"
              :key="option.value"
              :value="option.value"
            >
              {{ option.label }}
            </option>
          </select>
        </WithLabel>

        <WithLabel :label="t('APPOINTMENT_REMINDER_SETTINGS.TEMPLATES.STATUS')">
          <div class="flex items-center gap-3">
            <Switch v-model="formActive" />
            <span class="text-sm text-n-slate-11">
              {{
                formActive
                  ? t('APPOINTMENT_REMINDER_SETTINGS.TEMPLATES.ACTIVE')
                  : t('APPOINTMENT_REMINDER_SETTINGS.TEMPLATES.INACTIVE')
              }}
            </span>
          </div>
        </WithLabel>

        <div class="flex gap-2">
          <NextButton
            blue
            type="submit"
            :is-loading="isSubmitting"
            :label="
              editingTemplate
                ? t('APPOINTMENT_REMINDER_SETTINGS.TEMPLATES.UPDATE')
                : t('APPOINTMENT_REMINDER_SETTINGS.TEMPLATES.CREATE')
            "
          />
          <NextButton
            faded
            :label="t('APPOINTMENT_REMINDER_SETTINGS.TEMPLATES.CANCEL')"
            @click="closeForm"
          />
        </div>
      </form>
    </div>
  </SectionLayout>
</template>

<style scoped>
.form-group {
  width: 100%;
}

.message-container {
  display: flex;
  flex-direction: column;
  width: 100%;
}

.textarea-wrapper {
  position: relative;
  width: 100%;
}

.message-textarea {
  width: 100%;
  padding: 12px;
  font-size: 14px;
  line-height: 1.5;
  border: 1px solid var(--color-border);
  border-radius: 8px;
  background: var(--color-background);
  color: var(--color-body);
  resize: vertical;
  min-height: 100px;
  transition: all 0.2s ease;
}

.message-textarea:focus {
  outline: none;
  border-color: var(--w-500);
  box-shadow: 0 0 0 3px rgba(31, 147, 255, 0.1);
}

.message-textarea.expanded {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 90vw;
  max-width: 800px;
  height: 70vh;
  min-height: 400px;
  z-index: 1000;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
}

.message-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 999;
}

.expand-btn {
  position: absolute;
  top: 8px;
  right: 8px;
  padding: 6px;
  background: var(--color-background);
  border: 1px solid var(--color-border);
  border-radius: 6px;
  color: var(--s-600);
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
}

.expand-btn.expanded-btn {
  position: fixed;
  top: calc(50% - 35vh + 8px);
  right: calc(50% - min(45vw, 400px) + 8px);
  z-index: 1001;
}

.expand-btn:hover {
  background: var(--s-100);
  color: var(--s-800);
}

.dark .expand-btn:hover {
  background: var(--s-700);
  color: var(--s-200);
}

.variables-grid {
  display: grid;
  gap: 8px;
  width: 100%;
}

.variable-item {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 8px 10px;
  background: var(--s-50);
  border-radius: 8px;
  border: 1px solid var(--s-100);
}

.dark .variable-item {
  background: var(--s-900);
  border-color: var(--s-800);
}

.variable-btn {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  background: var(--w-50);
  border: 1px solid var(--w-200);
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;
  flex-shrink: 0;
}

.variable-btn:hover {
  background: var(--w-100);
  border-color: var(--w-300);
}

.dark .variable-btn {
  background: var(--w-900);
  border-color: var(--w-700);
}

.dark .variable-btn:hover {
  background: var(--w-800);
}

.variable-code {
  font-family: monospace;
  font-size: 11px;
  color: var(--w-700);
}

.dark .variable-code {
  color: var(--w-300);
}

.variable-desc {
  font-size: 12px;
  color: var(--s-600);
  line-height: 1.4;
  padding-top: 4px;
}

.dark .variable-desc {
  color: var(--s-400);
}
</style>
